import { useCallback, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ResourceType } from '@cotera/api';
import { Assert } from '@cotera/utilities';
import { toast } from '@cotera/client/app/components/ui';
import { useTenantedClient, useWhoami } from '@cotera/client/app/stores/org';
import { queryKeys } from '@cotera/client/app/hooks/query-cache-keys';
import { useWorkflowModel } from './workflow.model';
import { useWorkflowGraphMutations } from './use-workflow-graph-mutations';
import { draftStateToGraphCreateInput } from './workflow-draft-to-graph-create-input';
import {
  applyNestedHierarchyEdit,
  runWorkflowNodeInput,
  type NestedHierarchyEdit,
} from './nested-workflow-hierarchy-edit';
import { readWorkflowResource } from './workflow-resource-read';

export type NestedHierarchyEditor = {
  /** Applies one hierarchy edit to `workflowId`. Errors are toasted, not thrown. */
  readonly applyEdit: (
    workflowId: string,
    edit: NestedHierarchyEdit
  ) => Promise<void>;
  /**
   * Whether edits to `workflowId` land in the editor's draft (and so autosave
   * with everything else) rather than saving on the spot. Drives the copy — a
   * change that commits immediately should say so.
   */
  readonly editsAreDrafted: (workflowId: string) => boolean;
  /** Workflow ids with an edit in flight, so affordances can disable. */
  readonly pending: ReadonlySet<string>;
};

/**
 * Routes a hierarchy edit to whichever write path owns the workflow.
 *
 * Two paths, and the split is forced rather than chosen. The workflow the page
 * holds a model for **must** go through that model: the canvas renders its draft
 * and autosaves it, so a direct write would be silently reverted by the next
 * autosave. Every other workflow in the tree has no model, so it is written
 * straight through — re-fetched first, edited, and updated whole.
 *
 * Both paths run the same `draft*` helpers (see `applyNestedHierarchyEdit`), so
 * the two cannot disagree about what an edit means.
 */
export function useNestedWorkflowHierarchyEdits(): NestedHierarchyEditor {
  const model = useWorkflowModel();
  const mutations = useWorkflowGraphMutations();
  const client = useTenantedClient();
  const queryClient = useQueryClient();
  const orgId = useWhoami((s) => s.org.id);
  const userId = useWhoami((s) => s.whoami?.id) ?? '';
  const [pending, setPending] = useState<ReadonlySet<string>>(new Set());
  // The guard reads a ref, not `pending`: state is a snapshot, so two clicks in
  // one batch would both see it empty and both write. `pending` exists only so
  // affordances can render as busy.
  const inFlightRef = useRef<Set<string>>(new Set());

  const editsAreDrafted = useCallback(
    (workflowId: string) => workflowId === model.workflowId,
    [model.workflowId]
  );

  const applyThroughModel = useCallback(
    async (edit: NestedHierarchyEdit): Promise<void> => {
      switch (edit.kind) {
        case 'repoint':
          await mutations.updateNodeData(
            edit.nodeId,
            runWorkflowNodeInput(edit)
          );
          return;
        case 'remove':
          await mutations.deleteWorkflowNode(edit.nodeId);
          return;
        default:
          return Assert.unreachable(edit);
      }
    },
    [mutations]
  );

  const applyThroughApi = useCallback(
    async (workflowId: string, edit: NestedHierarchyEdit): Promise<void> => {
      const queryKey = queryKeys.resource.get({
        orgId,
        type: ResourceType.Workflow,
        id: workflowId,
      });
      // Re-read before writing. The update replaces the whole graph, and the copy
      // in the tree may be up to the query's stale window old — refetching keeps
      // the window between read and write to this call rather than to however
      // long the tree has been open. It also carries the workflow's current name
      // and idempotency key through, both of which `update` would otherwise
      // overwrite with whatever we last saw.
      const fresh = await queryClient.fetchQuery({
        queryKey,
        queryFn: () =>
          client.resource.get({ type: ResourceType.Workflow, id: workflowId }),
        staleTime: 0,
      });
      const workflow = readWorkflowResource(fresh);
      if (workflow === null) {
        toast.error('Could not read that workflow');
        return;
      }
      const { draft, baseline } = applyNestedHierarchyEdit({
        graph: workflow.graph,
        ctx: { workflowId, orgId, userId },
        edit,
        idempotencyKey: workflow.idempotencyKey,
      });

      const result = await client.resource.workflow.update({
        id: workflowId,
        name: workflow.name,
        graph: draftStateToGraphCreateInput(draft, baseline),
        idempotencyKey: workflow.idempotencyKey,
      });
      if (result.isErr()) {
        toast.error('Failed to save that workflow');
        return;
      }
      await queryClient.invalidateQueries({ queryKey });
    },
    [client, orgId, queryClient, userId]
  );

  const applyEdit = useCallback(
    async (workflowId: string, edit: NestedHierarchyEdit): Promise<void> => {
      if (inFlightRef.current.has(workflowId)) {
        return;
      }
      inFlightRef.current.add(workflowId);
      setPending((previous) => new Set(previous).add(workflowId));
      try {
        if (editsAreDrafted(workflowId)) {
          await applyThroughModel(edit);
        } else {
          await applyThroughApi(workflowId, edit);
        }
      } finally {
        inFlightRef.current.delete(workflowId);
        setPending((previous) => {
          const next = new Set(previous);
          next.delete(workflowId);
          return next;
        });
      }
    },
    [applyThroughApi, applyThroughModel, editsAreDrafted]
  );

  return { applyEdit, editsAreDrafted, pending };
}
